import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { GpsDataModel } from '@/app/models/gpsDataModel'
import { TmsDataModel } from '@/app/models/tmsDataModel'
import { GpsFromTachographModel } from '@/app/models/gpsFromTachographModel'
import { CountryModel } from '@/app/models/timesheet/countryModel'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { GpsDataType } from '@/app/constants/gpsDataType'
import { TIME_FORMAT } from '@/app/constants/date'
import { tmsEventKey } from '@/app/common/tmsDataHelper'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { DateField } from '@/ui/form/controls/DateField'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { toast } from '@/lib/toasts/api'
import { FaIcon } from '@/lib/fa'
import { getCountryFlag } from '@/app/services/country/countryFlagResolver'
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons'

type ImportSource = 'gps' | 'tms' | 'tachograph'

interface CombinedRow {
  id: string
  source: ImportSource
  date: Moment
  country: CountryModel | null
  isArrival: boolean
  isTransit: boolean
  checked: boolean
  gpsData?: GpsDataModel
  tmsData?: TmsDataModel
  isTmsUnloading?: boolean
  tachographData?: GpsFromTachographModel
}

interface Vehicle {
  id: string
  registrationNumber: string
}

interface CombinedImportViewProps {
  companyId: string
  driverId: number
  dateFrom: Moment
  dateTo: Moment
  vehicles: Vehicle[]
  onClose: () => void
  onSave: (events: EventModel[]) => void
  translate: (key: string) => string
}

const ITEMS_PER_PAGE = 10

const SOURCE_LABELS: Record<ImportSource, string> = {
  gps: 'GPS',
  tms: 'TMS',
  tachograph: 'Tachograf',
}

const SOURCE_BADGE_CLASSES: Record<ImportSource, string> = {
  gps: 'tw-bg-blue-100 tw-text-blue-700',
  tms: 'tw-bg-orange-100 tw-text-orange-700',
  tachograph: 'tw-bg-purple-100 tw-text-purple-700',
}

function normalizeGpsRows(data: GpsDataModel[]): CombinedRow[] {
  return data.map((item) => ({
    id: `gps-${item.id}`,
    source: 'gps' as ImportSource,
    date: moment(item.arriveDate || item.leaveDate),
    country: item.foreignCountry || item.destinationCountry || null,
    isArrival: !!item.arriveDate,
    isTransit: item.transit || false,
    checked: false,
    gpsData: item,
  }))
}

function normalizeTmsRows(data: TmsDataModel[]): CombinedRow[] {
  const rows: CombinedRow[] = []
  data.forEach((item, idx) => {
    if (item.loadingDate && item.loadingCountry) {
      rows.push({
        id: `tms-${item.id ?? idx}-loading`,
        source: 'tms',
        date: moment(item.loadingDate),
        country: item.loadingCountry || null,
        isArrival: false,
        isTransit: false,
        checked: false,
        tmsData: item,
        isTmsUnloading: false,
      })
    }
    if (item.unloadingDate && item.unloadingCountry) {
      rows.push({
        id: `tms-${item.id ?? idx}-unloading`,
        source: 'tms',
        date: moment(item.unloadingDate),
        country: item.unloadingCountry || null,
        isArrival: true,
        isTransit: false,
        checked: false,
        tmsData: item,
        isTmsUnloading: true,
      })
    }
  })
  return rows
}

function normalizeTachographRows(data: GpsFromTachographModel[]): CombinedRow[] {
  return data
    .filter((item) => !item.isHidden)
    .map((item) => ({
      id: `tachograph-${item.id ?? Math.random()}`,
      source: 'tachograph' as ImportSource,
      date: moment(item.date),
      country: item.country || null,
      isArrival: item.isArrival ?? false,
      isTransit: item.isTransit ?? false,
      checked: false,
      tachographData: item,
    }))
}

export const CombinedImportView: React.FC<CombinedImportViewProps> = ({
  companyId,
  driverId,
  dateFrom: initialDateFrom,
  dateTo: initialDateTo,
  vehicles,
  onClose,
  onSave,
  translate,
}) => {
  const { getGpsData, getVehicleGpsData, getTmsData, getGpsFromTachograph } =
    useBusinessTripService()

  const [allRows, setAllRows] = React.useState<CombinedRow[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState<string>(
    initialDateFrom.format('YYYY-MM-DD'),
  )
  const [dateTo, setDateTo] = React.useState<string>(
    initialDateTo.format('YYYY-MM-DD'),
  )

  // Source toggles
  const [showGps, setShowGps] = React.useState(true)
  const [showTms, setShowTms] = React.useState(true)
  const [showTachograph, setShowTachograph] = React.useState(true)

  // Arrival/departure filters (GPS + Tachograph)
  const [showArrivals, setShowArrivals] = React.useState(true)
  const [showDepartures, setShowDepartures] = React.useState(true)

  // Transit toggle for all tachograph rows
  const [checkAllTransit, setCheckAllTransit] = React.useState(false)

  // GPS vehicle mode
  const [vehicleMode, setVehicleMode] = React.useState(false)
  const [selectedVehicle, setSelectedVehicle] = React.useState<Vehicle | null>(null)

  const [currentPage, setCurrentPage] = React.useState(1)

  // Apply checkAllTransit to tachograph rows
  React.useEffect(() => {
    setAllRows((prev) =>
      prev.map((row) =>
        row.source === 'tachograph'
          ? { ...row, isTransit: checkAllTransit }
          : row,
      ),
    )
  }, [checkAllTransit])

  // Fetch all data when dates or vehicle mode changes
  React.useEffect(() => {
    if (!dateFrom || !dateTo) {
      setAllRows([])
      return
    }
    if (vehicleMode && !selectedVehicle) {
      setAllRows([])
      return
    }

    const fetchAll = async () => {
      setIsLoading(true)
      try {
        const [gpsData, tmsData, tachographData] = await Promise.allSettled([
          vehicleMode && selectedVehicle
            ? getVehicleGpsData({
                companyId,
                vehicleId: selectedVehicle.id,
                startTime: dateFrom,
                endTime: dateTo,
              })
            : getGpsData({
                companyId,
                driverId: String(driverId),
                startTime: dateFrom,
                endTime: dateTo,
              }),
          getTmsData({
            companyId,
            driverId: String(driverId),
            startTime: dateFrom,
            endTime: dateTo,
          }),
          getGpsFromTachograph({
            companyId,
            driverId: String(driverId),
            startTime: dateFrom,
            endTime: dateTo,
          }),
        ])

        const gpsRows =
          gpsData.status === 'fulfilled'
            ? normalizeGpsRows(gpsData.value || [])
            : []

        const tmsRows =
          tmsData.status === 'fulfilled'
            ? normalizeTmsRows(tmsData.value || [])
            : []

        const tachographRows =
          tachographData.status === 'fulfilled'
            ? normalizeTachographRows(tachographData.value || [])
            : []

        if (gpsData.status === 'rejected') {
          console.error('GPS fetch failed:', gpsData.reason)
        }
        if (tmsData.status === 'rejected') {
          console.error('TMS fetch failed:', tmsData.reason)
        }
        if (tachographData.status === 'rejected') {
          console.error('Tachograph fetch failed:', tachographData.reason)
        }

        const combined = [...gpsRows, ...tmsRows, ...tachographRows].sort(
          (a, b) => {
            if (!a.date.isValid()) return 1
            if (!b.date.isValid()) return -1
            return a.date.valueOf() - b.date.valueOf()
          },
        )

        setAllRows(combined)
        setCurrentPage(1)
      } catch (err: any) {
        console.error('Error fetching combined import data:', err)
        toast.error({
          text: err?.message || translate('errorCodes.retrieving-data-failed'),
        })
        setAllRows([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, driverId, dateFrom, dateTo, vehicleMode, selectedVehicle])

  // Filtered rows based on toggles
  const filteredRows = React.useMemo(() => {
    return allRows.filter((row) => {
      // Source filter
      if (row.source === 'gps' && !showGps) return false
      if (row.source === 'tms' && !showTms) return false
      if (row.source === 'tachograph' && !showTachograph) return false

      // Arrival/departure filter applies to GPS and tachograph
      if (row.source === 'gps' || row.source === 'tachograph') {
        if (!showArrivals && row.isArrival) return false
        if (!showDepartures && !row.isArrival) return false
      }

      return true
    })
  }, [allRows, showGps, showTms, showTachograph, showArrivals, showDepartures])

  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE)
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  )

  const allChecked =
    filteredRows.length > 0 && filteredRows.every((row) => row.checked)

  const handleToggleAll = (checked: boolean) => {
    const filteredIds = new Set(filteredRows.map((r) => r.id))
    setAllRows((prev) =>
      prev.map((row) =>
        filteredIds.has(row.id) ? { ...row, checked } : row,
      ),
    )
  }

  const handleToggleRow = (id: string, checked: boolean) => {
    setAllRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, checked } : row)),
    )
  }

  const handleToggleTransit = (id: string, isTransit: boolean) => {
    setAllRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, isTransit } : row)),
    )
  }

  const handleVehicleModeChange = (enabled: boolean) => {
    setVehicleMode(enabled)
    if (!enabled) setSelectedVehicle(null)
  }

  const handleSave = () => {
    const selectedRows = allRows.filter((row) => row.checked)
    if (selectedRows.length === 0) {
      toast.error({
        text:
          translate('newDelegation.no-items-selected'),
      })
      return
    }

    const events: EventModel[] = []
    const existingDates: string[] = []
    const existingTmsKeys = new Set<string>()
    let lpCounter = 0

    for (const row of selectedRows) {
      if (!row.date.isValid()) continue

      if (row.source === 'gps' && row.gpsData) {
        const item = row.gpsData
        if (!item.arriveDate && !item.leaveDate) continue
        const eventDate = item.arriveDate || item.leaveDate
        if (!eventDate) continue
        const dateStr = moment(eventDate).format(TIME_FORMAT)
        if (existingDates.includes(dateStr)) continue

        let tripEvent = TripEventType.ChangeCountry
        if (item.type === GpsDataType.Begin || item.isBeginOrEnd) {
          tripEvent = TripEventType.Begin
        }

        events.push({
          date: moment(eventDate),
          isTransit: row.isTransit,
          tripEvent,
          country: item.foreignCountry || item.destinationCountry || null,
          city: item.city || undefined,
          lp: lpCounter++,
        })
        existingDates.push(dateStr)
      } else if (row.source === 'tms' && row.tmsData) {
        const item = row.tmsData
        if (row.isTmsUnloading) {
          if (!item.unloadingDate || !item.unloadingCountry) continue
          const tmsKey = tmsEventKey(TripEventType.Unloading, item.docNumber, item.unloadingDate)
          if (existingTmsKeys.has(tmsKey)) continue
          events.push({
            date: moment(item.unloadingDate),
            tripEvent: TripEventType.Unloading,
            country: item.unloadingCountry,
            docNumber: item.docNumber || undefined,
            tmsRouteType: item.routeType,
            secondDate: item.loadingDate ? moment(item.loadingDate) : null,
            secondCountry: item.loadingCountry || null,
            lp: lpCounter++,
          })
          existingTmsKeys.add(tmsKey)
        } else {
          if (!item.loadingDate || !item.loadingCountry) continue
          const tmsKey = tmsEventKey(TripEventType.Loading, item.docNumber, item.loadingDate)
          if (existingTmsKeys.has(tmsKey)) continue
          events.push({
            date: moment(item.loadingDate),
            tripEvent: TripEventType.Loading,
            country: item.loadingCountry,
            docNumber: item.docNumber || undefined,
            tmsRouteType: item.routeType,
            secondDate: item.unloadingDate ? moment(item.unloadingDate) : null,
            secondCountry: item.unloadingCountry || null,
            lp: lpCounter++,
          })
          existingTmsKeys.add(tmsKey)
        }
      } else if (row.source === 'tachograph' && row.tachographData) {
        const item = row.tachographData
        if (!item.country || !item.date) continue
        const dateStr = moment(item.date).format(TIME_FORMAT)
        if (existingDates.includes(dateStr)) continue
        events.push({
          date: moment(item.date),
          isTransit: row.isTransit,
          tripEvent: TripEventType.ChangeCountry,
          country: item.country,
          lp: lpCounter++,
        })
        existingDates.push(dateStr)
      }
    }

    if (events.length > 0) {
      onSave(events)
      toast.success({
        text: translate('newDelegation.gps-events-created'),
      })
    } else {
      toast.warning({
        text:
          translate('newDelegation.no-events-created'),
      })
    }
  }

  const startIndex = filteredRows.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0
  const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, filteredRows.length)

  const activeSources = [
    showGps && 'GPS',
    showTachograph && 'Z tachografu',
    showTms && 'TMS',
  ]
    .filter(Boolean)
    .join(' / ')

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-white">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-4 tw-border-b tw-bg-[#fafbff] tw-px-6 tw-py-4">
        <button
          onClick={onClose}
          className="tw-flex tw-items-center tw-gap-2 tw-rounded tw-p-2 hover:tw-bg-gray-100"
        >
          <FaIcon icon={faArrowLeft} className="tw-h-4 tw-w-4" />
        </button>
        <h2 className="tw-text-lg tw-font-semibold">
          {activeSources || 'Importuj dane'}
        </h2>
      </div>

      {/* Filters */}
      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-6 tw-border-b tw-px-6 tw-py-4">
        {/* Date range */}
        <div className="tw-w-[180px]">
          <DateField
            label={translate('newDelegation.dateFrom')}
            value={dateFrom}
            setValue={(val) => setDateFrom(val || '')}
            placeholder="DD-MM-RRRR"
          />
        </div>
        <div className="tw-w-[180px]">
          <DateField
            label={translate('newDelegation.dateTo')}
            value={dateTo}
            setValue={(val) => setDateTo(val || '')}
            placeholder="DD-MM-RRRR"
          />
        </div>

        {/* Arrival/departure/transit filters */}
        <div className="tw-flex tw-items-center tw-gap-4 tw-pb-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="showArrivals"
              checked={showArrivals}
              onChange={(e) => setShowArrivals(e.target.checked)}
            />
            <label
              htmlFor="showArrivals"
              className="tw-cursor-pointer tw-text-sm tw-text-content-base"
            >
              {translate('gpsFromTachograph.showCountryArrival')}
            </label>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="showDepartures"
              checked={showDepartures}
              onChange={(e) => setShowDepartures(e.target.checked)}
            />
            <label
              htmlFor="showDepartures"
              className="tw-cursor-pointer tw-text-sm tw-text-content-base"
            >
              {translate('gpsFromTachograph.showCountryLeave')}
            </label>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="checkAllTransit"
              checked={checkAllTransit}
              onChange={(e) => setCheckAllTransit(e.target.checked)}
            />
            <label
              htmlFor="checkAllTransit"
              className="tw-cursor-pointer tw-text-sm tw-text-content-base"
            >
              {translate('gpsFromTachograph.checkUncheckTransitRows')}
            </label>
          </div>
        </div>

        {/* Source toggles */}
        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-4 tw-pb-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="showTachograph"
              checked={showTachograph}
              onChange={(e) => setShowTachograph(e.target.checked)}
            />
            <label
              htmlFor="showTachograph"
              className="tw-cursor-pointer tw-text-sm tw-font-medium tw-text-purple-700"
            >
              Tachograf
            </label>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="showTms"
              checked={showTms}
              onChange={(e) => setShowTms(e.target.checked)}
            />
            <label
              htmlFor="showTms"
              className="tw-cursor-pointer tw-text-sm tw-font-medium tw-text-orange-700"
            >
              TMS
            </label>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="showGps"
              checked={showGps}
              onChange={(e) => setShowGps(e.target.checked)}
            />
            <label
              htmlFor="showGps"
              className="tw-cursor-pointer tw-text-sm tw-font-medium tw-text-blue-700"
            >
              GPS
            </label>
          </div>
        </div>
      </div>

      {/* GPS vehicle mode row */}
      {showGps && (
        <div className="tw-flex tw-items-center tw-gap-4 tw-border-b tw-px-6 tw-py-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="vehicleMode"
              checked={vehicleMode}
              onChange={(e) => handleVehicleModeChange(e.target.checked)}
            />
            <label
              htmlFor="vehicleMode"
              className="tw-cursor-pointer tw-text-sm tw-text-content-base"
            >
              {translate('newDelegation.vehicle')}
            </label>
          </div>
          {vehicleMode && (
            <div className="tw-w-[220px]">
              <select
                value={selectedVehicle?.id ? String(selectedVehicle.id) : ''}
                onChange={(e) => {
                  const vehicle = vehicles.find((v) => String(v.id) === e.target.value)
                  setSelectedVehicle(vehicle || null)
                }}
                className="tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
              >
                <option value="">
                  {translate('newDelegation.choose-vehicle')}
                </option>
                {vehicles.map((vehicle) => (
                  <option key={String(vehicle.id)} value={String(vehicle.id)}>
                    {vehicle.registrationNumber}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="tw-flex-1 tw-overflow-auto">
        {isLoading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span className="tw-text-content-secondary">
              {translate('gridPager.loadingText')}
            </span>
          </div>
        ) : paginatedRows.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span className="tw-text-content-secondary">
              {translate('businessTrip.no-data')}
            </span>
          </div>
        ) : (
          <table className="tw-w-full tw-table-auto">
            <thead>
              <tr className="tw-border-b tw-bg-[#f2f2f2]/25">
                <th className="tw-w-10 tw-p-2">
                  <Checkbox
                    checked={allChecked}
                    onChange={(e) => handleToggleAll(e.target.checked)}
                  />
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  Data
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('businessTrip.source')}
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('gpsDownload.foreignCountry')}
                </th>
                <th className="tw-p-2 tw-text-center tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('gpsFromTachograph.showCountryArrival')}
                </th>
                <th className="tw-p-2 tw-text-center tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('gpsDownload.transit')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row) => (
                <tr
                  key={row.id}
                  className={`tw-border-b ${
                    row.checked
                      ? 'tw-bg-teal-50 hover:tw-bg-teal-100'
                      : 'hover:tw-bg-gray-50'
                  }`}
                >
                  <td className="tw-p-2">
                    <Checkbox
                      checked={row.checked}
                      onChange={(e) => handleToggleRow(row.id, e.target.checked)}
                    />
                  </td>
                  <td className="tw-p-2 tw-text-sm tw-text-content-base">
                    {row.date.isValid()
                      ? row.date.format(TIME_FORMAT)
                      : '-'}
                  </td>
                  <td className="tw-p-2">
                    <span
                      className={`tw-inline-block tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium ${SOURCE_BADGE_CLASSES[row.source]}`}
                    >
                      {SOURCE_LABELS[row.source]}
                    </span>
                  </td>
                  <td className="tw-p-2">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      {row.country?.code && (
                        <img
                          src={getCountryFlag(row.country.code)}
                          alt={row.country.code}
                          className="tw-h-4 tw-w-6 tw-object-cover"
                          onError={(e) => {
                            ;(e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      )}
                      <span className="tw-text-sm tw-text-content-base">
                        {row.country?.name || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="tw-p-2 tw-text-center">
                    {row.source !== 'tms' ? (
                      <span
                        className={`tw-text-sm ${
                          row.isArrival ? 'tw-text-green-600' : 'tw-text-gray-400'
                        }`}
                      >
                        {row.isArrival ? '✓' : '–'}
                      </span>
                    ) : (
                      <span className="tw-text-sm tw-text-gray-400">–</span>
                    )}
                  </td>
                  <td className="tw-p-2 tw-text-center">
                    <Checkbox
                      checked={row.isTransit}
                      onChange={(e) => handleToggleTransit(row.id, e.target.checked)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {filteredRows.length > 0 && (
        <div className="tw-flex tw-items-center tw-justify-between tw-border-t tw-px-6 tw-py-4">
          <span className="tw-text-sm tw-text-content-secondary">
            {startIndex}-{endIndex} {translate('gridPager.of')}{' '}
            {filteredRows.length}
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="tw-rounded tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-text-sm disabled:tw-opacity-50"
            >
              <FaIcon icon={faAnglesLeft} className="tw-h-3 tw-w-3" />
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="tw-rounded tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-text-sm disabled:tw-opacity-50"
            >
              <FaIcon icon={faChevronLeft} className="tw-h-3 tw-w-3" />
            </button>
            <span className="tw-px-2 tw-text-sm">
              {currentPage} / {totalPages || 1}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages || totalPages === 0}
              className="tw-rounded tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-text-sm disabled:tw-opacity-50"
            >
              <FaIcon icon={faChevronRight} className="tw-h-3 tw-w-3" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="tw-rounded tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-text-sm disabled:tw-opacity-50"
            >
              <FaIcon icon={faAnglesRight} className="tw-h-3 tw-w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-4 tw-border-t tw-px-6 tw-py-4">
        <button
          onClick={onClose}
          className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-50"
        >
          {translate('answer.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-50"
        >
          {translate('menu.save')}
        </button>
      </div>
    </div>
  )
}
